import { forwardRef } from "react"
import ImageWithBasePath from "../../../../core/common/imageWithBasePath"
import dayjs from "dayjs"
import { useSelector } from "react-redux"
import { selectBranches, selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice"
import { campusBrandName } from "../../../../utils/campusLogos"

interface StudentSidebarProps {
  registrationData?: {
    registrationNumber: string
    firstName: string
    gender: string
    status: string
    createdAt: string
    dateOfBirth: string
    fatherEmail: string
    fatherPhoneNumber: string
    _id: string
    lastName: string
    nationality: string
    religion: string
    motherTongue: string
    studentPicture: string
    appliedClass: { name: string; _id: string; registrationType: string }
    fatherName: string
    testGroup: {
      testDateTime?: string
    } | null
    noOfSiblings: string
    // The registration's OWN campus — preferred over whatever's globally
    // selected in the header when present.
    branch?: string
  }
}

const StudentSidebar = forwardRef<HTMLDivElement, StudentSidebarProps>(
  (
    {
      registrationData = {
        registrationNumber: "",
        firstName: "",
        gender: "",
        status: "",
        createdAt: "",
        dateOfBirth: "",
        fatherEmail: "",
        fatherPhoneNumber: "",
        _id: "",
        lastName: "",
        nationality: "",
        religion: "",
        motherTongue: "",
        studentPicture: "",
      },
    },
    ref
  ) => {
    const branches = useSelector(selectBranches)
    const currentBranch = useSelector(selectCurrentBranch)
    const recordBranch =
      branches.find((b) => b._id === registrationData.branch) || currentBranch
    return (
      <>
        <div className="col-xxl-3 col-xl-4 theiaStickySidebar">
          <div className="stickybar pb-4">
            <div className="card border-white">
              <div className="card-header">
                <div className="d-flex align-items-center flex-wrap row-gap-3">
                  <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                    <ImageWithBasePath
                      fullSrc={registrationData.studentPicture}
                      src="assets/img/students/student-01.jpg"
                      className="img-fluid"
                      alt="img"
                    />
                  </div>
                  <div className="overflow-hidden">
                    <span
                      className={`badge badge-soft-${
                        registrationData.status === "accepted"
                          ? "success"
                          : registrationData.status === "rejected"
                          ? "danger"
                          : "warning"
                      } d-inline-flex align-items-center mb-1`}
                    >
                      <i className="ti ti-circle-filled fs-5 me-1" />
                      {registrationData.status}
                    </span>
                    <h5 className="mb-1 text-truncate">
                      {`${registrationData.firstName} ${registrationData.lastName}`}
                    </h5>
                    <p className="text-primary">
                      {registrationData.registrationNumber}
                    </p>
                  </div>
                </div>
              </div>
              {/* Basic Information */}
              <div className="card-body">
                <h5 className="mb-3">Basic Information</h5>
                <dl className="row mb-0">
                  <dt className="col-6 fw-medium text-dark mb-3">
                    Reistration No
                  </dt>
                  <dd className="col-6 mb-3">
                    {registrationData.registrationNumber}
                  </dd>
                  <dt className="col-6 fw-medium text-dark mb-3">Gender</dt>
                  <dd className="col-6 mb-3">{registrationData.gender}</dd>
                  <dt className="col-6 fw-medium text-dark mb-3">
                    Date Of Birth
                  </dt>
                  <dd className="col-6 mb-3">
                    {new Date(
                      registrationData.dateOfBirth
                    ).toLocaleDateString()}
                  </dd>
                  <dt className="col-6 fw-medium text-dark mb-3">
                    Nationality
                  </dt>
                  <dd className="col-6 mb-3">{registrationData.nationality}</dd>
                  <dt className="col-6 fw-medium text-dark mb-3">Religion</dt>
                  <dd className="col-6 mb-3">{registrationData.religion}</dd>
                  <dt className="col-6 fw-medium text-dark mb-3">
                    Mother Tongue
                  </dt>
                  <dd className="col-6 mb-3">
                    {registrationData.motherTongue}
                  </dd>
                </dl>
                {/* <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_fees_collect"
                  className="btn btn-primary btn-sm w-100"
                >
                  Add Fees
                </Link> */}
              </div>
              {/* /Basic Information */}
            </div>
            {/* Primary Contact Info */}
            <div className="card border-white">
              <div className="card-body">
                <h5 className="mb-3">Primary Contact Info</h5>
                <div className="d-flex align-items-center mb-3">
                  <a
                    href={`tel:${registrationData.fatherPhoneNumber?.replace(
                      /[^\d+]/g,
                      ""
                    )}`}
                    className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default hover:bg-light-400"
                    title="Click to call"
                  >
                    <i className="ti ti-phone" />
                  </a>
                  <div>
                    <span className="text-dark fw-medium mb-1">
                      Phone Number
                    </span>
                    <p>
                      {registrationData.fatherPhoneNumber || "Not available"}
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <a
                    href={`mailto:${registrationData.fatherEmail}`}
                    className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default hover:bg-light-400"
                    title="Send email"
                  >
                    <i className="ti ti-mail" />
                  </a>
                  <div>
                    <span className="text-dark fw-medium mb-1">
                      Email Address
                    </span>
                    <p className="break-all">
                      {registrationData.fatherEmail || "Not available"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* /Primary Contact Info */}
            {/* Sibiling Information */}
            <div className="card border-white">
              <div className="card-body">
                <h5 className="mb-3">Sibiling Information</h5>
                <span>{registrationData.noOfSiblings}</span>
                {/* <div className="d-flex align-items-center bg-light-300 rounded p-3 mb-3">
                  <span className="avatar avatar-lg">
                    <ImageWithBasePath
                      src="assets/img/students/student-06.jpg"
                      className="img-fluid rounded"
                      alt="img"
                    />
                  </span>
                  <div className="ms-2">
                    <h5 className="fs-14">Ralph Claudia</h5>
                    <p>III, B</p>
                  </div>
                </div> */}
                {/* <div className="d-flex align-items-center bg-light-300 rounded p-3">
                  <span className="avatar avatar-lg">
                    <ImageWithBasePath
                      src="assets/img/students/student-07.jpg"
                      className="img-fluid rounded"
                      alt="img"
                    />
                  </span>
                  <div className="ms-2">
                    <h5 className="fs-14">Julie Scott</h5>
                    <p>V, A</p>
                  </div>
                </div> */}
              </div>
            </div>
            {/* /Sibiling Information */}
            {/* Transport Information */}
            {/* <div className="card border-white mb-0">
            <div className="card-body pb-1">
              <ul className="nav nav-tabs nav-tabs-bottom mb-3">
                <li className="nav-item">
                  <Link
                    className="nav-link active"
                    to="#hostel"
                    data-bs-toggle="tab"
                  >
                    Hostel
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="#transport" data-bs-toggle="tab">
                    Transportation
                  </Link>
                </li>
              </ul>
              <div className="tab-content">
                <div className="tab-pane fade show active" id="hostel">
                  <div className="d-flex align-items-center mb-3">
                    <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                      <i className="ti ti-building-fortress fs-16" />
                    </span>
                    <div>
                      <h6 className="fs-14 mb-1">HI-Hostel, Floor</h6>
                      <p className="text-primary">Room No : 25</p>
                    </div>
                  </div>
                </div>
                <div className="tab-pane fade" id="transport">
                  <div className="d-flex align-items-center mb-3">
                    <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                      <i className="ti ti-bus fs-16" />
                    </span>
                    <div>
                      <span className="fs-12 mb-1">Route</span>
                      <p className="text-dark">Newyork</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="mb-3">
                        <span className="fs-12 mb-1">Bus Number</span>
                        <p className="text-dark">AM 54548</p>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="mb-3">
                        <span className="fs-12 mb-1">Pickup Point</span>
                        <p className="text-dark">Cincinatti</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
            {/* /Transport Information */}
          </div>
        </div>
        <div className="modal fade">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <div className="d-flex align-items-center">
                  <h4 className="modal-title">Admit Card</h4>
                </div>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              {/* This div will be printed */}
              <div className="modal-body">
                <div
                  id="printable-content"
                  className="printable-content"
                  ref={ref}
                >
                  <div className="d-flex justify-content-center align-items-center mb-2">
                    <div className="logo-small img">
                      {recordBranch?.blackLogoUrl ? (
                        <img src={recordBranch.blackLogoUrl} alt="Logo" />
                      ) : (
                        <ImageWithBasePath
                          src="assets/img/icons/logo.png"
                          alt="Logo"
                        />
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <h1 className="fw-bold fs-1">{campusBrandName(recordBranch).toUpperCase()}</h1>
                      <p className="text-center">
                        {currentBranch?.address || "Plot# 54, Block-3, Bahadurabad, Karachi. Ph:0"}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <h3 className="fw-bold mb-0 border border-light p-3 text-center col-6">
                      Admit Card for Test/Interview
                    </h3>
                    <div className=" border border-light p-1 col-6">
                      <div className="fw-bold border-bottom">
                        Registration# {registrationData?.registrationNumber}
                      </div>
                      <span className="fw-bold">Form# R.SN#</span>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <p>
                      Received Registration Form for admission to Class/Grade:
                    </p>
                    <div className="border-bottom mb-3">
                      {registrationData?.appliedClass?.name || "___________"}
                    </div>
                  </div>
                  <table className="table table-bordered mb-3">
                    <tbody>
                      <tr>
                        <td className="fw-bold">1. Name of Student</td>
                        <td>{`${registrationData?.firstName || ""} ${
                          registrationData?.lastName || ""
                        }`}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">2. Father's Name</td>
                        <td>{registrationData?.fatherName || ""}</td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="mb-4 d-flex gap-2">
                    <p>
                      Parents are requested to come along with the child for
                      Written Test/Interview on
                    </p>
                    <div className="d-flex justify-content-center gap-3 mb-3">
                      <div className="border-bottom text-center px-3">
                        {registrationData?.testGroup?.testDateTime
                          ? dayjs(
                              registrationData?.testGroup?.testDateTime
                            ).format("DD-MM-YYYY")
                          : "___________"}
                      </div>
                      <span>at</span>
                      <div className="border-bottom text-center px-3">
                        {registrationData?.testGroup?.testDateTime
                          ? (() => {
                              const date = dayjs(
                                registrationData.testGroup?.testDateTime
                              )
                              return date.isValid()
                                ? date.format("hh:mm A")
                                : "Invalid Time"
                            })()
                          : "___________"}
                      </div>
                    </div>
                  </div>

                  <div className="text-end mt-5">
                    <div className="border-top pt-2 d-inline-block">
                      <p className="mb-0">Signature of Admin</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }
)

export default StudentSidebar
